import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Calendar, Mail, ExternalLink, Globe2, FileText } from "lucide-react";

const SECTIONS = [
  {
    id: "commitment",
    title: "Our commitment",
    body: (
      <>
        <p>
          Goodwin Living is committed to ensuring digital accessibility for
          people with disabilities. We are continually improving the user
          experience for everyone and applying the relevant accessibility
          standards.
        </p>
        <p>
          The Goodwin Resolve Employee Relations platform is part of this
          commitment. We design, build, and test against the{" "}
          <a
            href="https://www.w3.org/TR/WCAG21/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
            style={{ color: "var(--page-fg)" }}
          >
            Web Content Accessibility Guidelines (WCAG) 2.1 Level AA
          </a>{" "}
          published by the World Wide Web Consortium (W3C).
        </p>
      </>
    ),
  },
  {
    id: "conformance-status",
    title: "Conformance status",
    body: (
      <>
        <p>
          The Goodwin Resolve application is{" "}
          <strong style={{ color: "var(--page-fg)" }}>
            partially conformant
          </strong>{" "}
          with WCAG 2.1 level AA. Partially conformant means that some parts
          of the content do not yet fully conform to the accessibility
          standard.
        </p>
        <ul className="ml-5 list-disc space-y-1.5">
          <li>
            All authenticated pages targeting the WCAG 2.1 AA standard pass
            axe-core automated audits with{" "}
            <strong>zero serious or critical violations</strong> in both light
            and dark themes.
          </li>
          <li>
            All interactive components meet a minimum contrast ratio of{" "}
            <strong>4.5:1</strong> for body text and{" "}
            <strong>3:1</strong> for large text and non-text contrast.
          </li>
          <li>
            All form fields are paired with a programmatic{" "}
            <code
              className="rounded px-1 py-0.5 text-[11px]"
              style={{
                backgroundColor: "var(--surface-2)",
                border: "1px solid var(--border-subtle)",
              }}
            >
              &lt;label&gt;
            </code>{" "}
            or accessible name.
          </li>
          <li>
            A skip-to-content link is the first focusable element on every
            authenticated page.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "features",
    title: "Accessibility features",
    body: (
      <ul className="ml-5 list-disc space-y-1.5">
        <li>
          Full keyboard support — Tab order matches reading order, Esc closes
          dialogs, and a visible 2-pixel focus ring is rendered on every
          interactive element via{" "}
          <code
            className="rounded px-1 py-0.5 text-[11px]"
            style={{
              backgroundColor: "var(--surface-2)",
              border: "1px solid var(--border-subtle)",
            }}
          >
            :focus-visible
          </code>
          .
        </li>
        <li>
          Light and dark themes — Each user can select Light, Dark, or System
          from their profile. Both themes meet WCAG 2.1 AA contrast.
        </li>
        <li>
          Screen-reader friendly markup — every chart includes a parallel{" "}
          <code
            className="rounded px-1 py-0.5 text-[11px]"
            style={{
              backgroundColor: "var(--surface-2)",
              border: "1px solid var(--border-subtle)",
            }}
          >
            role="img"
          </code>{" "}
          description, every table uses semantic{" "}
          <code
            className="rounded px-1 py-0.5 text-[11px]"
            style={{
              backgroundColor: "var(--surface-2)",
              border: "1px solid var(--border-subtle)",
            }}
          >
            &lt;th scope&gt;
          </code>
          , and dialogs trap focus while open.
        </li>
        <li>
          Reduced-motion support — animation utilities respect{" "}
          <code
            className="rounded px-1 py-0.5 text-[11px]"
            style={{
              backgroundColor: "var(--surface-2)",
              border: "1px solid var(--border-subtle)",
            }}
          >
            prefers-reduced-motion
          </code>
          .
        </li>
      </ul>
    ),
  },
  {
    id: "limitations",
    title: "Known limitations",
    body: (
      <ul className="ml-5 list-disc space-y-1.5">
        <li>
          Some legacy uploaded PDF documents may not include a complete
          accessibility tag tree. A flagged copy can be requested from your HR
          partner.
        </li>
        <li>
          A small number of decorative chart fills are reported as{" "}
          <em>incomplete</em> (rather than failing) by automated tooling
          because they are alpha-blended; manual review confirms compliant
          on-screen contrast for the visible labels.
        </li>
      </ul>
    ),
  },
  {
    id: "assessment",
    title: "Assessment approach",
    body: (
      <>
        <p>
          Goodwin Living assessed the accessibility of Goodwin Resolve through
          the methodology of self-evaluation, supplemented by automated
          testing using axe-core 4.10 and manual keyboard, screen-reader, and
          colour-contrast audits.
        </p>
        <p>
          Detailed audit notes are maintained internally in{" "}
          <code
            className="rounded px-1 py-0.5 text-[11px]"
            style={{
              backgroundColor: "var(--surface-2)",
              border: "1px solid var(--border-subtle)",
            }}
          >
            /docs/ACCESSIBILITY.md
          </code>{" "}
          and are reviewed each release.
        </p>
      </>
    ),
  },
  {
    id: "feedback",
    title: "Feedback",
    body: (
      <>
        <p>
          We welcome your feedback on the accessibility of Goodwin Resolve.
          Please let us know if you encounter accessibility barriers:
        </p>
        <ul className="ml-5 list-disc space-y-1.5">
          <li>
            E-mail:{" "}
            <a
              href="mailto:accessibility@goodwinliving.org"
              className="underline"
              style={{ color: "var(--page-fg)" }}
            >
              accessibility@goodwinliving.org
            </a>
          </li>
          <li>Phone: 1 (703) 824-1400 (HR Service Center)</li>
          <li>
            Postal address: 4800 Fillmore Avenue, Alexandria, VA 22311, USA
          </li>
        </ul>
        <p>We try to respond to feedback within 5 business days.</p>
      </>
    ),
  },
];

export default function AccessibilityStatement() {
  const today = new Date().toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div
      data-testid="accessibility-statement-page"
      className="min-h-screen"
      style={{
        backgroundColor: "var(--page-bg)",
        color: "var(--page-fg)",
      }}
    >
      <a
        href="#a11y-main"
        data-testid="skip-to-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[100] focus:rounded-md focus:px-3 focus:py-2 focus:text-white"
        style={{ backgroundColor: "#1E3A5F" }}
      >
        Skip to main content
      </a>

      <header
        className="border-b"
        style={{ borderColor: "var(--border-subtle)" }}
      >
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4">
          <Link
            to="/"
            data-testid="accessibility-statement-home-link"
            className="flex items-center gap-2 text-sm font-medium"
            style={{ color: "var(--page-fg-secondary)" }}
          >
            <span
              className="font-serif text-[15px] font-semibold"
              style={{ color: "var(--page-fg)", letterSpacing: "-0.005em" }}
            >
              Goodwin
            </span>
            <span
              className="text-[10px] font-extrabold uppercase tracking-[0.14em]"
              style={{ color: "var(--success-fg)" }}
            >
              Resolve
            </span>
          </Link>
          <span
            className="inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-[0.08em]"
            style={{ color: "var(--page-fg-muted)" }}
          >
            <Globe2 className="h-3.5 w-3.5" strokeWidth={1.7} />
            Public · Accessibility
          </span>
        </div>
      </header>

      <main
        id="a11y-main"
        tabIndex={-1}
        className="mx-auto max-w-3xl px-6 py-10"
      >
        <div className="mb-8">
          <span
            className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em]"
            style={{
              backgroundColor: "var(--success-bg)",
              color: "var(--success-fg)",
              boxShadow: "inset 0 0 0 1px var(--success-border)",
            }}
            data-testid="accessibility-statement-badge"
          >
            <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.8} />
            WCAG 2.1 AA
          </span>
          <h1
            className="mt-4 font-serif text-4xl font-semibold tracking-tight sm:text-5xl"
            style={{ color: "var(--page-fg)", letterSpacing: "-0.02em" }}
            data-testid="accessibility-statement-title"
          >
            Accessibility statement
          </h1>
          <p
            className="mt-3 text-base leading-relaxed"
            style={{ color: "var(--page-fg-secondary)" }}
          >
            Goodwin Living believes that everyone should be able to use the
            tools that support our team. This page summarises how we apply
            accessibility standards to Goodwin Resolve.
          </p>
          <div
            className="mt-4 inline-flex items-center gap-1.5 text-[11px]"
            style={{ color: "var(--page-fg-muted)" }}
            data-testid="accessibility-statement-date"
          >
            <Calendar className="h-3.5 w-3.5" strokeWidth={1.7} />
            Last updated {today}
          </div>
        </div>

        <div className="space-y-10">
          {SECTIONS.map((section) => (
            <section
              key={section.id}
              data-testid={`accessibility-section-${section.id}`}
              aria-labelledby={`heading-${section.id}`}
            >
              <h2
                id={`heading-${section.id}`}
                className="font-serif text-xl font-semibold"
                style={{
                  color: "var(--page-fg)",
                  letterSpacing: "-0.012em",
                }}
              >
                {section.title}
              </h2>
              <div
                className="mt-3 space-y-3 text-[14px] leading-relaxed"
                style={{ color: "var(--page-fg-secondary)" }}
              >
                {section.body}
              </div>
            </section>
          ))}
        </div>

        <hr
          className="my-10"
          style={{ borderColor: "var(--border-subtle)" }}
        />

        <footer
          data-testid="accessibility-statement-footer"
          className="rounded-xl px-4 py-4"
          style={{
            backgroundColor: "var(--surface-2)",
            border: "1px solid var(--border-subtle)",
          }}
        >
          <p
            className="text-[11px] uppercase tracking-[0.10em]"
            style={{ color: "var(--page-fg-muted)" }}
          >
            Methodology
          </p>
          <p
            className="mt-1.5 text-[13px] leading-relaxed"
            style={{ color: "var(--page-fg-secondary)" }}
          >
            This statement was prepared using the W3C{" "}
            <a
              href="https://www.w3.org/WAI/planning/statements/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 underline"
              style={{ color: "var(--page-fg)" }}
              data-testid="accessibility-statement-w3c-link"
            >
              Accessibility Statement Generator
              <ExternalLink className="h-3 w-3" strokeWidth={1.8} />
            </a>{" "}
            methodology and reviewed against WCAG 2.1 Level AA success
            criteria. Audit details are versioned alongside the application
            source code.
          </p>
          <div
            className="mt-3 flex flex-wrap items-center gap-3 text-[11px]"
            style={{ color: "var(--page-fg-muted)" }}
          >
            <span className="inline-flex items-center gap-1">
              <FileText className="h-3 w-3" strokeWidth={1.8} /> WCAG 2.1
              Level AA
            </span>
            <span aria-hidden>·</span>
            <span className="inline-flex items-center gap-1">
              <Mail className="h-3 w-3" strokeWidth={1.8} />
              <a
                href="mailto:accessibility@goodwinliving.org"
                className="underline"
                style={{ color: "var(--page-fg-secondary)" }}
              >
                accessibility@goodwinliving.org
              </a>
            </span>
            <span aria-hidden>·</span>
            <Link
              to="/login"
              className="underline"
              data-testid="accessibility-statement-login-link"
              style={{ color: "var(--page-fg-secondary)" }}
            >
              Return to sign in
            </Link>
          </div>
        </footer>
      </main>
    </div>
  );
}
